import React, { useEffect, useState, useMemo } from 'react';
import axiosInstance from './axiosInstance';
import './dashboard.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const Dashboard = () => {
  const [deviceStats, setDeviceStats] = useState({
    totalDevices: 0,
    devicesOnline: 0,
    devicesOffline: 0,
  });
  const [deviceLoading, setDeviceLoading] = useState(true);
  const [deviceError, setDeviceError] = useState(null);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const [mediaSummary, setMediaSummary] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState(null);

  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState(null);

  const [collapsedSections, setCollapsedSections] = useState({
    deviceMonitoring: false,
    userManagement: false,
    mediaInfo: false,
    supportTickets: false,
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AA336A'];

  const fetchDeviceStats = async () => {
    try {
      const response = await axiosInstance.get('/api/deviceStats');
      setDeviceStats(response.data);
    } catch (error) {
      console.error('Error fetching device stats:', error);
      setDeviceError('Failed to load device statistics.');
    } finally {
      setDeviceLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axiosInstance.get('/api/allUsers');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching all users:', error);
      setUsersError('Failed to load user data.');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchMediaSummary = async () => {
    try {
      const response = await axiosInstance.get('/api/mediaInfo/stats');
      setMediaSummary(response.data);
    } catch (error) {
      console.error('Error fetching media summary:', error);
      setMediaError('Failed to load media statistics.');
    } finally {
      setMediaLoading(false);
    }
  };

  const fetchSupportTickets = async () => {
    try {
      const response = await axiosInstance.get('/api/open-incidents');
      setSupportTickets(response.data);
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      setTicketsError('Failed to load support tickets.');
    } finally {
      setTicketsLoading(false);
    }
  };

  const userSummary = useMemo(() => {
    const summary = {
      totalUsers: users.length,
      roles: {},
    };

    users.forEach((user) => {
      if (Array.isArray(user.user_roles)) {
        user.user_roles.forEach((role) => {
          summary.roles[role] = (summary.roles[role] || 0) + 1;
        });
      }
    });

    return summary;
  }, [users]);

  const processedMediaSummary = useMemo(() => {
    if (!mediaSummary) return null;

    return {
      totalMedia: mediaSummary.totalMedia,
      mediaTypes: Object.entries(mediaSummary.typeBreakdown).map(([name, value]) => ({
        name,
        value,
      })),
    };
  }, [mediaSummary]);

  const deviceSummary = useMemo(() => ({
    totalDevices: deviceStats.totalDevices,
    devicesOnline: deviceStats.devicesOnline,
    devicesOffline: deviceStats.devicesOffline,
  }), [deviceStats]);

  const supportTicketsSummary = useMemo(() => {
    const summary = {
      totalTickets: supportTickets.length,
      ticketsByStatus: {},
    };

    supportTickets.forEach((ticket) => {
      const status = ticket.fields.status.name || 'Unknown';
      summary.ticketsByStatus[status] = (summary.ticketsByStatus[status] || 0) + 1;
    });

    return summary;
  }, [supportTickets]);

  const toggleSection = (section) => {
    setCollapsedSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  useEffect(() => {
    fetchDeviceStats();
    fetchAllUsers();
    fetchMediaSummary();
    fetchSupportTickets();
  }, []);

  return (
    <div className="dashboard-container">
      <h1>Dashboard</h1>
      <div className="dashboard-cards">
        {/* Device Monitoring */}
        <div className="dashboard-card">
          <h2 onClick={() => toggleSection('deviceMonitoring')} className="collapsible-header">
            Device Monitoring
          </h2>
          {!collapsedSections.deviceMonitoring && (
            <div className="card-content">
              {deviceLoading ? (
                <p>Loading...</p>
              ) : deviceError ? (
                <p className="error">{deviceError}</p>
              ) : (
                <>
                  <p><strong>Total Devices:</strong> {deviceSummary.totalDevices}</p>
                  <p><strong>Devices Online:</strong> {deviceSummary.devicesOnline}</p>
                  <p><strong>Devices Offline:</strong> {deviceSummary.devicesOffline}</p>
                  <div className="chart-item">
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Online', value: deviceSummary.devicesOnline },
                            { name: 'Offline', value: deviceSummary.devicesOffline },
                          ]}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          label
                        >
                          <Cell fill="#00C49F" />
                          <Cell fill="#FF8042" />
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* User Management */}
        <div className="dashboard-card">
          <h2 onClick={() => toggleSection('userManagement')} className="collapsible-header">
            User Management
          </h2>
          {!collapsedSections.userManagement && (
            <div className="card-content">
              {usersLoading ? (
                <p>Loading...</p>
              ) : usersError ? (
                <p className="error">{usersError}</p>
              ) : (
                <>
                  <p><strong>Total Users:</strong> {userSummary.totalUsers}</p>
                  <div className="chart-item">
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart
                        layout="vertical"
                        data={Object.entries(userSummary.roles).map(([name, value]) => ({
                          name,
                          value,
                        }))}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="value" fill="#0088FE" name="Users" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Media Info */}
        <div className="dashboard-card">
          <h2 onClick={() => toggleSection('mediaInfo')} className="collapsible-header">
            Media Information
          </h2>
          {!collapsedSections.mediaInfo && (
            <div className="card-content">
              {mediaLoading ? (
                <p>Loading...</p>
              ) : mediaError ? (
                <p className="error">{mediaError}</p>
              ) : (
                <>
                  <p><strong>Total Media:</strong> {processedMediaSummary.totalMedia}</p>
                  <div className="chart-item">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={processedMediaSummary.mediaTypes}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          label
                        >
                          {processedMediaSummary.mediaTypes.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Support Tickets */}
        <div className="dashboard-card">
          <h2 onClick={() => toggleSection('supportTickets')} className="collapsible-header">
            Support Tickets
          </h2>
          {!collapsedSections.supportTickets && (
            <div className="card-content">
              {ticketsLoading ? (
                <p>Loading...</p>
              ) : ticketsError ? (
                <p className="error">{ticketsError}</p>
              ) : (
                <>
                  <p><strong>Total Tickets:</strong> {supportTicketsSummary.totalTickets}</p>
                  <div className="chart-item">
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={Object.entries(supportTicketsSummary.ticketsByStatus).map(([name, value]) => ({
                            name,
                            value,
                          }))}
                          dataKey="value"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          label
                        >
                          {Object.entries(supportTicketsSummary.ticketsByStatus).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
